import http from './http';

// Service de l'écran « Google Sites » — suivi du parc (projet google-sites-install_P1, B4_01,
// contrat C7 produit par B1_02 : api/src/controllers/hosting_detected_controller.js).
//
// Deux routes suadmin (ensureAdmin) :
//   GET  /suadmin/hosting-detected?page_nbr=&page_size=&q=&activity=
//                                                             → { sites, total, last_full_analysis_at, remaining,
//                                                                 inactive_after_days }   (B4_02)
//   POST /suadmin/hosting-detected/analyze { onlyNew }        → { analyzed, detected, failed, failed_website_ids,
//                                                                remaining, threads_total_sum, threads_empty_sum,
//                                                                comments_trapped_sum, last_full_analysis_at }
//
// http.js préfixe déjà REACT_APP_API_URL (qui CONTIENT `/api`) et pose Authorization: <gc_token>.
// ⛔ BASE sans `/api` — sinon `/api/api/…` → 404 (motif blocsAffichesService.js:14).
// Signature réelle de http.get = un seul argument `path` → query string construite à la main.

const BASE = '/suadmin/hosting-detected';

// Taille de page figée en v1 (défaut de l'API, plafond 200 — hostingDetection.js:66-67).
export const PAGE_SIZE = 50;

// ⛔ GARDE « HTTP 200 + { gcCode: 410 } » = session expirée, PAS une donnée.
// routes.js pose `suadmin.use(auth.ensureAuthenticated)` AVANT `ensureAdmin`, et ce middleware
// répond 200 + gcCode sur un JWT absent/expiré ; http.js ne lève que sur `!res.ok`. Sans cette
// garde, l'écran afficherait « aucun site » (des zéros crédibles) sur une session morte, et un
// clic « Lancer l'analyse » annoncerait un lot analysé alors que rien n'est parti.
// Pattern : blocsAffichesService.rejectGcCode (piège de la chaîne littérale 'undefined' inclus).
export const SESSION_EXPIRED = 'session_expired';

function rejectGcCode(result) {
  if (result === undefined || result === null || typeof result !== 'object') {
    const error = new Error('réponse inattendue du serveur (corps non JSON).');
    error.code = 'unexpected_response';
    throw error;
  }
  const hasErr = result.err && result.err !== 'undefined';
  if (result.gcCode || hasErr) {
    const error = new Error(
      'session expirée ou accès refusé. Recharge la page pour te reconnecter.'
    );
    error.code = SESSION_EXPIRED;
    error.body = result;
    throw error;
  }
  return result;
}

// Liste paginée (1-based, D46) des sites au constat « hébergement Google Sites », recherche `q`
// par nom OU adresse (D45 — regex non ancrée, insensible à la casse ; ni _id ni clé publique).
// Réponse IMBRIQUÉE : sites[i] = { website: {_id, name, url, public_key, base_path}, hosting_detected: {...} }.
// `base_path` toujours servi (`/` par défaut, C7 amendé — B5_04) ; son ABSENCE signe une API d'avant
// B5_04 → adresse via `websiteAddress(site, { absentKey: 'unknown' })` (`src/lib/websiteAddress.js`).
//
// B4_02 — `activity` (`active` | `inactive`) filtre CÔTÉ SERVEUR sur le dernier commentaire du
// site : un filtre d'écran ne verrait que les 50 lignes de la page. `all` (ou toute autre valeur)
// n'envoie RIEN : la requête par défaut reste celle de B4_01, octet pour octet. La réponse porte
// alors `inactive_after_days` ; son ABSENCE signe une API d'avant B4_02, qui ignore le paramètre.
export const getSites = ({ q = '', page = 1, activity = 'all' } = {}) => {
  const params = [
    `page_nbr=${encodeURIComponent(page)}`,
    `page_size=${encodeURIComponent(PAGE_SIZE)}`
  ];
  const search = String(q || '').trim();
  if (search) params.push(`q=${encodeURIComponent(search)}`);
  if (activity === 'active' || activity === 'inactive') params.push(`activity=${activity}`);
  return http.get(`${BASE}?${params.join('&')}`).then(rejectGcCode);
};

// UN lot d'analyse borné côté API (200 sites / 25 s par défaut) — jamais une boucle.
// onlyNew:true (défaut) = sites jamais analysés ; false = rejoue aussi les plus vieux constats.
export const analyze = ({ onlyNew = true } = {}) =>
  http.post(`${BASE}/analyze`, { onlyNew: Boolean(onlyNew) }).then(rejectGcCode);

// 409 « analysis already running » : http.js:9-11 lève `Error('HTTP 409: {"msg":…}')` sur tout
// `!res.ok`, AVANT que rejectGcCode voie le corps. Le seul discriminant est le PRÉFIXE du message
// (err.status n'existe pas dans cette couche).
export const isAnalysisRunning = (err) =>
  Boolean(err && typeof err.message === 'string' && /^HTTP 409\b/.test(err.message));

const hostingDetectedService = { getSites, analyze, isAnalysisRunning, SESSION_EXPIRED, PAGE_SIZE };
export default hostingDetectedService;
